import { useCallback } from 'react';
import ErrorHelpers from '~/app/errorHelpers.js';

/**
 * usePDFAttach
 *
 * Owns the two PDF-attachment methods that were previously class methods
 * on FormViewer:
 *
 *   addOrAttachDocument(...) — calls ReturnPDFAttach to add or attach a doc
 *   getDocumentIDByVar(var)  — looks up a document_id by document_status_var
 *
 * Returns:
 *   { addOrAttachDocument, getDocumentIDByVar }
 */
const usePDFAttach = ({
  calcAPIRef,
  // Kept-current refs
  returnIDRef, // kept-current ref to props.returnID
  formNavRef, // kept-current ref to useFormNavigation result
  snackbarsRef, // kept-current ref to useSnackbars result
  attachmentsRef, // kept-current ref to props.attachments
}) => {
  /**
   * Attaches or adds a document to the return via ReturnPDFAttach.
   * When `blob` is provided the document is newly uploaded; when `blob` is
   * null/undefined an existing document is attached by `docid`.
   *
   * @param {SyntheticEvent} e
   * @param {string}  btnid           button ID that triggered the attach
   * @param {string}  efcode          EF code
   * @param {string}  description     document description
   * @param {*}       blob            file blob, or null/undefined for attach
   * @param {string}  imageDescription
   * @param {string}  imageName
   * @param {string}  imageType
   * @param {string}  attachmentType
   * @param {string}  docid           existing document ID (attach path only)
   */
  // TODO --> We should send the button ID around instead and just pass back the User Response Back
  const addOrAttachDocument = useCallback(
    async (
      e,
      btnid,
      efcode,
      description,
      blob,
      imageDescription,
      imageName,
      imageType,
      attachmentType,
      docid,
      binaryAttachment,
      dataBin,
      dataBinSig,
    ) => {
      e?.persist?.();
      const activeForm = formNavRef.current.activeForm;
      const interviewMode = formNavRef.current.interviewMode;

      const hasBlob = blob != null;

      if (!hasBlob) return;

      try {
        calcAPIRef.current.ReturnPDFAttach(
          returnIDRef.current,
          btnid,
          efcode,
          description,
          activeForm,
          blob,
          imageName,
          imageDescription,
          imageType,
          attachmentType,
          docid ?? 0,
          interviewMode,
          binaryAttachment,
          dataBin,
          dataBinSig,
        );
      } catch (err) {
        snackbarsRef.current.showErrorSnackbar(err);
        ErrorHelpers.handleError('Error: ', err);
      }
    },
    [calcAPIRef, formNavRef, returnIDRef, snackbarsRef],
  );

  /**
   * Finds the document_id for the attachment whose document_status_var
   * matches `docvar`. Returns an empty string if not found.
   *
   * @param {string} docvar
   * @returns {string}
   */
  const getDocumentIDByVar = useCallback(
    docvar => {
      let docID = '';
      attachmentsRef.current.forEach(attach => {
        if (Object.prototype.hasOwnProperty.call(attach, 'document_status_var')) {
          if (attach.document_status_var === docvar) {
            docID = attach.document_id;
          }
        }
      });
      return docID;
    },
    [attachmentsRef],
  );

  return {
    addOrAttachDocument,
    getDocumentIDByVar,
  };
};

export default usePDFAttach;
